import React, { createContext, useContext, useState } from 'react';

export const TABS = {
  CAMPAIGNS: 'campaigns',
  ACTIONS: 'actions',
  SETTINGS: 'settings',
} as const;

export type AutomationToolTab = (typeof TABS)[keyof typeof TABS];

export interface AutomationToolboxContextProps {
  currentTab: AutomationToolTab;
  setCurrentTab: (tab: AutomationToolTab) => void;
  handleTabChange: (nextTab: AutomationToolTab) => void;
  pendingTabChange: AutomationToolTab | null;
  setPendingTabChange: (tab: AutomationToolTab | null) => void;
  brandSettingsDirty: boolean;
  setBrandSettingsDirty: (dirty: boolean) => void;
  brandSettingsValid: boolean;
  setBrandSettingsValid: (valid: boolean) => void;
  isInitialSetup: boolean;
  setIsInitialSetup: (value: boolean) => void;
}

export const AutomationToolboxContext = createContext<AutomationToolboxContextProps | undefined>(
  undefined,
);

export const useAutomationToolbox = () => {
  const context = useContext(AutomationToolboxContext);

  if (!context) {
    throw new Error('useAutomationContext must be used within an AutomationProvider');
  }

  return context;
};

export const AutomationToolboxProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentTab, setCurrentTab] = useState<AutomationToolTab>(TABS.SETTINGS);
  const [pendingTabChange, setPendingTabChange] = useState<AutomationToolTab | null>(null);
  const [brandSettingsDirty, setBrandSettingsDirty] = useState(false);
  const [brandSettingsValid, setBrandSettingsValid] = useState(true);
  const [isInitialSetup, setIsInitialSetup] = useState(false);

  const handleTabChange = (newTab: AutomationToolTab) => {
    if ((!brandSettingsValid || brandSettingsDirty) && currentTab === TABS.SETTINGS) {
      setPendingTabChange(newTab);
    } else {
      setCurrentTab(newTab);
    }
  };

  return (
    <AutomationToolboxContext.Provider
      value={{
        currentTab,
        setCurrentTab,
        handleTabChange,
        pendingTabChange,
        setPendingTabChange,
        brandSettingsDirty,
        setBrandSettingsDirty,
        brandSettingsValid,
        setBrandSettingsValid,
        isInitialSetup,
        setIsInitialSetup,
      }}
    >
      {children}
    </AutomationToolboxContext.Provider>
  );
};
